import { useCallback, useEffect, useRef, useState } from 'react';

// Only devices with a real pointer get hover previews. A touch screen reports
// `hover: none`, where a "hover" is really a tap and autoplaying on it would be
// both surprising and expensive on mobile data.
export const canHover = () => {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  try {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  } catch (e) {
    return false;
  }
};

/**
 * True only once the pointer (or keyboard focus) has rested on something for
 * `delay` ms. Sweeping the mouse across a grid of fifty cards must not start
 * fifty video loads, so nothing is reported until the user has actually stopped
 * on one; leaving cancels a pending intent immediately.
 */
export default (delay = 400, enabled = true) => {
  const [active, setActive] = useState(false);
  const timer = useRef(null);
  const allowed = useRef(null);

  if (allowed.current === null) allowed.current = canHover();

  const clear = useCallback(() => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (!enabled || !allowed.current || timer.current) return;
    timer.current = setTimeout(() => {
      timer.current = null;
      setActive(true);
    }, delay);
  }, [delay, enabled]);

  const stop = useCallback(() => {
    clear();
    setActive(false);
  }, [clear]);

  // A card can be scrolled out from under a resting pointer, or unmounted by a
  // refetch, with no leave event ever arriving.
  useEffect(() => clear, [clear]);

  useEffect(() => {
    if (!enabled) stop();
  }, [enabled, stop]);

  // `stop` is handed back alongside the DOM handlers so a caller can dismiss the
  // hover state for a reason the DOM never reports - opening a dialog over the
  // element, for one, where focus moves away without a blur the element sees.
  return [active, { onMouseEnter: start, onMouseLeave: stop, onFocus: start, onBlur: stop, stop }];
};
